import { predicate, propertySearch, searchItemFinder } from '@/utilities/itemFinderSearch.utilities';
import { sqTemplatesApi } from '@seeqdev/api/endpoint/TemplatesApi';
import { OperatorEnum } from '@seeqdev/api/model/PropertyFilterInputV1';
import { SeeqNames } from '@seeqdev/constants';
import type { TemplateOutputV1 } from '@seeqdev/api/model/TemplateOutputV1';

export interface SystemTemplateMessages {
  /** The error message when no system template has the name */
  notFound: string;
  /** The error message when more than one system template has the name */
  multipleFound: string;
}

/**
 * Fetches a template that Seeq installs with the system, such as the model behind an ML tool, by its exact name.
 * Exactly one system template must have the name.
 */
export async function fetchSystemTemplate(name: string, messages: SystemTemplateMessages): Promise<TemplateOutputV1> {
  const { items } = await searchItemFinder({
    propertySearches: [
      propertySearch({
        types: [SeeqNames.Types.ModelContainer],
        predicates: [
          predicate(SeeqNames.Properties.Name, OperatorEnum.EQUALS, name),
          predicate(
            SeeqNames.Properties.DatasourceClass,
            OperatorEnum.EQUALS,
            SeeqNames.LocalDatasources.Templates.DatasourceClass,
          ),
          predicate(
            SeeqNames.Properties.DatasourceId,
            OperatorEnum.EQUALS,
            SeeqNames.LocalDatasources.Templates.System.DatasourceId,
          ),
        ],
      }),
    ],
    // Two is enough to tell an ambiguous match from a unique one
    limit: 2,
  });

  if (items.length === 0) {
    throw new Error(messages.notFound);
  }
  if (items.length > 1) {
    throw new Error(messages.multipleFound);
  }

  return (await sqTemplatesApi.getTemplate({ templateId: items[0].id })).data as TemplateOutputV1;
}
